import { GRID_COLS, GRID_ROWS, MAP_OFFSET_X, MAP_OFFSET_Y, TILE_SIZE } from "../config";
import type { MapDef } from "../maps/maps";
import { loadPlacementMask } from "../systems/PlacementMasks";
import { loadPathOverride } from "../systems/PathOverrides";
import { loadFoliageOcclusions, type FoliageZone } from "../systems/FoliageOcclusions";

export type TileKind =
  | "grass"
  | "path"
  | "rock"
  | "fountain"
  | "torch"
  | "banner"
  | "pine"
  | "water"
  | "ice_shard"
  | "castle-gate";

export interface MapData {
  cols: number;
  rows: number;
  tileSize: number;
  offsetX: number;
  offsetY: number;
  tiles: TileKind[][];                   // [row][col]
  /**
   * Cosmetic overlay drawn on top of grass cells. The cell's TileKind stays
   * `grass` (so it remains buildable); the overlay just changes how it looks
   * before any tower is placed. Cleared when a tower covers the cell.
   */
  decorOverlay?: (string | null)[][];
  waypoints: { x: number; y: number }[]; // pixel coords incl. offset
  /** Optional second path (e.g. river maps with two lanes). */
  altWaypoints?: { x: number; y: number }[];
  /** Half-width used when projecting the painted road into blocked grid cells. */
  pathBlockRadius: number;
  /** Aspect ratio of the authored background used for cover-fit projection. */
  backgroundAspect: number;
  /** Tiles flagged as un-buildable beyond just non-grass (e.g. river). */
  blocked?: Set<string>;
  /** Optional editor-authored mask. true/1 is buildable; false/0 is blocked. */
  placementMask?: boolean[][];
  /** Circular / polygonal zones where the painted background is redrawn above towers and enemies. */
  foliageOcclusions: FoliageZone[];
}

function makeBlankGrid(): TileKind[][] {
  const tiles: TileKind[][] = [];
  for (let r = 0; r < GRID_ROWS; r++) {
    tiles[r] = [];
    for (let c = 0; c < GRID_COLS; c++) tiles[r][c] = "grass";
  }
  return tiles;
}

function carveGridPath(tiles: TileKind[][], wpGrid: [number, number][]): void {
  for (let i = 0; i < wpGrid.length - 1; i++) {
    const [x1, y1] = wpGrid[i];
    const [x2, y2] = wpGrid[i + 1];
    if (x1 === x2) {
      const [a, b] = [Math.min(y1, y2), Math.max(y1, y2)];
      for (let y = a; y <= b; y++) carve(tiles, x1, y);
    } else if (y1 === y2) {
      const [a, b] = [Math.min(x1, x2), Math.max(x1, x2)];
      for (let x = a; x <= b; x++) carve(tiles, x, y1);
    }
  }
}

function gridToWaypoints(wpGrid: [number, number][]): { x: number; y: number }[] {
  return wpGrid.map(([c, r]) => ({
    x: MAP_OFFSET_X + c * TILE_SIZE + TILE_SIZE / 2,
    y: MAP_OFFSET_Y + r * TILE_SIZE + TILE_SIZE / 2
  }));
}

/**
 * If the alt path's final waypoint matches a point on the main path, extend
 * the alt path with the remainder of the main path. Without this, enemies on
 * the alt lane "reach the end" mid-map (where the lanes merge) and incorrectly
 * deduct lives instead of continuing toward the castle.
 */
function mergeAltWithMain(
  alt: [number, number][],
  main: [number, number][]
): [number, number][] {
  if (alt.length === 0) return alt;
  const [ec, er] = alt[alt.length - 1];
  // Scan backwards: if the main route revisits the join coordinate, continue
  // from the furthest occurrence so merged enemies never run back on themselves.
  let joinIdx = -1;
  for (let i = main.length - 1; i >= 0; i--) {
    if (main[i][0] === ec && main[i][1] === er) { joinIdx = i; break; }
  }
  if (joinIdx < 0 || joinIdx >= main.length - 1) return alt;
  return [...alt, ...main.slice(joinIdx + 1)];
}

export interface CoverProjection {
  renderWidth: number;
  renderHeight: number;
  left: number;
  top: number;
}

/**
 * Cover-fit a painted 16:9 battlefield into the wider gameplay strip without
 * stretching it. Both rendering and path projection use this exact transform,
 * keeping authored roads and enemy movement aligned.
 */
export function getCoverProjection(map: Pick<MapData, "cols" | "rows" | "tileSize" | "offsetX" | "offsetY" | "backgroundAspect">): CoverProjection {
  const targetWidth = map.cols * map.tileSize;
  const targetHeight = map.rows * map.tileSize;
  const targetAspect = targetWidth / targetHeight;
  const renderWidth = targetAspect >= map.backgroundAspect
    ? targetWidth
    : targetHeight * map.backgroundAspect;
  const renderHeight = targetAspect >= map.backgroundAspect
    ? targetWidth / map.backgroundAspect
    : targetHeight;
  return {
    renderWidth,
    renderHeight,
    left: map.offsetX + (targetWidth - renderWidth) / 2,
    top: map.offsetY + (targetHeight - renderHeight) / 2,
  };
}

function paintedToWaypoints(
  map: Pick<MapData, "cols" | "rows" | "tileSize" | "offsetX" | "offsetY" | "backgroundAspect">,
  points: [number, number][]
): { x: number; y: number }[] {
  const projection = getCoverProjection(map);
  return points.map(([u, v]) => ({
    x: projection.left + u * projection.renderWidth,
    y: projection.top + v * projection.renderHeight,
  }));
}

function distanceToSegmentSq(
  px: number,
  py: number,
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return (px - a.x) ** 2 + (py - a.y) ** 2;
  const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / (dx * dx + dy * dy)));
  const x = a.x + t * dx;
  const y = a.y + t * dy;
  return (px - x) ** 2 + (py - y) ** 2;
}

/** Rasterize a smooth painted route into logical path cells only. */
function carvePaintedPath(
  tiles: TileKind[][],
  waypoints: { x: number; y: number }[],
  radius: number
): void {
  const radiusSq = radius * radius;
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      const x = MAP_OFFSET_X + c * TILE_SIZE + TILE_SIZE / 2;
      const y = MAP_OFFSET_Y + r * TILE_SIZE + TILE_SIZE / 2;
      for (let i = 0; i < waypoints.length - 1; i++) {
        if (distanceToSegmentSq(x, y, waypoints[i], waypoints[i + 1]) <= radiusSq) {
          tiles[r][c] = "path";
          break;
        }
      }
    }
  }
}

function stampCastleGate(tiles: TileKind[][], waypoints: { x: number; y: number }[]): void {
  for (let i = waypoints.length - 1; i >= 0; i--) {
    const gc = Math.floor((waypoints[i].x - MAP_OFFSET_X) / TILE_SIZE);
    const gr = Math.floor((waypoints[i].y - MAP_OFFSET_Y) / TILE_SIZE);
    if (inBounds(gc, gr)) { tiles[gr][gc] = "castle-gate"; break; }
  }
}

/** Build a runtime MapData from a campaign MapDef. */
export function buildMapFromDef(def: MapDef): MapData {
  const tiles = makeBlankGrid();
  const backgroundAspect = def.backgroundAspect ?? 16 / 9;
  const pathBlockRadius = def.pathBlockRadius ?? TILE_SIZE * 0.78;
  const geometry = {
    cols: GRID_COLS,
    rows: GRID_ROWS,
    tileSize: TILE_SIZE,
    offsetX: MAP_OFFSET_X,
    offsetY: MAP_OFFSET_Y,
    backgroundAspect,
  };

  const override = loadPathOverride(def.key);
  const usesPaintedRoute = Boolean(override?.main ?? def.paintedWaypoints?.length);
  const mainSource = override?.main ?? def.paintedWaypoints ?? def.waypoints;
  // Override replaces the alt lane entirely (undefined = clear it).
  const altSource = override ? override.alt : (def.altPaintedWaypoints ?? def.altWaypoints);
  const mergedAltSource = altSource ? mergeAltWithMain(altSource, mainSource) : undefined;
  // Track whether mergeAltWithMain actually extended the alt path.
  const altWasMerged = Boolean(altSource && mergedAltSource && mergedAltSource.length > altSource.length);
  const waypoints = usesPaintedRoute
    ? paintedToWaypoints(geometry, mainSource)
    : gridToWaypoints(mainSource);
  const altWaypoints = mergedAltSource
    ? (usesPaintedRoute ? paintedToWaypoints(geometry, mergedAltSource) : gridToWaypoints(mergedAltSource))
    : undefined;

  if (usesPaintedRoute) {
    carvePaintedPath(tiles, waypoints, pathBlockRadius);
    if (altWaypoints) carvePaintedPath(tiles, altWaypoints, pathBlockRadius);
  } else {
    carveGridPath(tiles, def.waypoints);
    if (mergedAltSource) carveGridPath(tiles, mergedAltSource);
  }

  // A painted road can curve close enough to a tile center for the broad
  // collision corridor to classify an obvious visual clearing as path. Let
  // individual maps opt those reviewed cells back into tower placement.
  for (const [c, r] of def.buildable ?? []) {
    if (inBounds(c, r)) tiles[r][c] = "grass";
  }

  for (const d of def.decorations ?? []) {
    if (inBounds(d.c, d.r) && tiles[d.r][d.c] === "grass") tiles[d.r][d.c] = d.kind;
  }

  // Stamp castle-gate at last in-bounds main waypoint.
  stampCastleGate(tiles, waypoints);
  // Stamp castle-gate for an independent alt route (merged routes share main's gate).
  if (altWaypoints && !altWasMerged) stampCastleGate(tiles, altWaypoints);

  // Render blocked tiles as visual rocks too (so players see why they can't build).
  const blocked = new Set<string>();
  for (const [c, r] of def.blocked ?? []) {
    blocked.add(`${c},${r}`);
    if (inBounds(c, r) && tiles[r][c] === "grass") tiles[r][c] = "rock";
  }

  // Cosmetic biome fill: every leftover grass cell gets a themed overlay
  // texture so the whole map reads as forest / riverbank / glacier. The cell
  // stays `grass` so towers can still be placed there — the tower covers the
  // overlay visually.
  const decorOverlay = buildBiomeOverlay(def, tiles);

  return {
    cols: GRID_COLS,
    rows: GRID_ROWS,
    tileSize: TILE_SIZE,
    offsetX: MAP_OFFSET_X,
    offsetY: MAP_OFFSET_Y,
    tiles,
    decorOverlay,
    waypoints,
    altWaypoints,
    pathBlockRadius,
    backgroundAspect,
    blocked,
    placementMask: loadPlacementMask(def.key, GRID_COLS, GRID_ROWS) ?? undefined,
    foliageOcclusions: loadFoliageOcclusions(def.key) ?? def.foliageOcclusions ?? []
  };
}

/**
 * Per-biome cosmetic overlay table. For every grass cell we pick a texture
 * key (or null) using a deterministic seeded PRNG so the map looks consistent
 * across reloads. Path / decoration cells get null (already themed).
 */
function buildBiomeOverlay(def: MapDef, tiles: TileKind[][]): (string | null)[][] {
  const overlay: (string | null)[][] = [];
  const seed = hashString(def.key);
  const rand = mulberry32(seed);
  for (let r = 0; r < GRID_ROWS; r++) {
    overlay[r] = [];
    for (let c = 0; c < GRID_COLS; c++) {
      overlay[r][c] = tiles[r][c] === "grass" ? pickBiomeOverlay(def.biome, rand) : null;
    }
  }
  return overlay;
}

function pickBiomeOverlay(biome: MapDef["biome"], rand: () => number): string | null {
  const roll = rand();
  switch (biome) {
    case "plains":
      // Dense pine forest with occasional clearings (no overlay = open grass).
      if (roll < 0.55) return "tile-pine";
      if (roll < 0.62) return "tile-rock";
      return null;
    case "river":
      // Riverbank: scattered pines + occasional rocks. Bridge keep area stays clear.
      if (roll < 0.30) return "tile-pine";
      if (roll < 0.36) return "tile-rock";
      return null;
    case "snow":
      // Glacier: ice spires + rocky outcrops sprinkled across the snowfield.
      if (roll < 0.32) return "tile-ice-shard";
      if (roll < 0.40) return "tile-rock";
      return null;
    case "swamp":
      if (roll < 0.30) return "tile-rock";
      return null;
    case "mine":
      if (roll < 0.40) return "tile-rock";
      return null;
    case "desert":
      if (roll < 0.20) return "tile-rock";
      return null;
    case "siege":
      if (roll < 0.15) return "tile-rock";
      return null;
    default:
      return null;
  }
}

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Hand-crafted level: a winding path from left edge to right edge.
 * Returns tile grid + pixel-space waypoints for enemies.
 *
 * Kept for backwards-compatibility (tests / legacy entry). New code should use
 * `buildMapFromDef` with a MapDef from `src/maps/maps.ts`.
 */
export function buildDefaultMap(): MapData {
  const cols = GRID_COLS;
  const rows = GRID_ROWS;
  const tiles: TileKind[][] = makeBlankGrid();

  const wpGrid: [number, number][] = [
    [-1, 1],
    [4, 1],
    [4, 6],
    [9, 6],
    [9, 2],
    [14, 2],
    [14, 7],
    [cols, 7]
  ];

  carveGridPath(tiles, wpGrid);

  const rocks: [number, number][] = [[1, 0], [6, 4], [12, 0], [18, 4], [2, 8], [16, 8]];
  for (const [c, r] of rocks) {
    if (inBounds(c, r) && tiles[r][c] === "grass") tiles[r][c] = "rock";
  }

  const decorations: [number, number, TileKind][] = [
    [2,  3, "fountain"],
    [7,  0, "torch"],
    [11, 8, "torch"],
    [17, 0, "banner"],
    [3,  7, "banner"]
  ];
  for (const [c, r, kind] of decorations) {
    if (inBounds(c, r) && tiles[r][c] === "grass") tiles[r][c] = kind;
  }

  for (let i = wpGrid.length - 1; i >= 0; i--) {
    const [gc, gr] = wpGrid[i];
    if (inBounds(gc, gr)) {
      tiles[gr][gc] = "castle-gate";
      break;
    }
  }

  return {
    cols,
    rows,
    tileSize: TILE_SIZE,
    offsetX: MAP_OFFSET_X,
    offsetY: MAP_OFFSET_Y,
    tiles,
    waypoints: gridToWaypoints(wpGrid),
    pathBlockRadius: TILE_SIZE * 0.78,
    backgroundAspect: 16 / 9,
    foliageOcclusions: [],
  };
}

function carve(tiles: TileKind[][], c: number, r: number): void {
  if (inBounds(c, r)) tiles[r][c] = "path";
}

function inBounds(c: number, r: number): boolean {
  return c >= 0 && c < GRID_COLS && r >= 0 && r < GRID_ROWS;
}

export function tileAt(map: MapData, c: number, r: number): TileKind | null {
  if (c < 0 || r < 0 || c >= map.cols || r >= map.rows) return null;
  return map.tiles[r][c];
}

export function pixelToTile(map: MapData, x: number, y: number): { c: number; r: number } {
  return {
    c: Math.floor((x - map.offsetX) / map.tileSize),
    r: Math.floor((y - map.offsetY) / map.tileSize)
  };
}

export function tileCenter(map: MapData, c: number, r: number): { x: number; y: number } {
  return {
    x: map.offsetX + c * map.tileSize + map.tileSize / 2,
    y: map.offsetY + r * map.tileSize + map.tileSize / 2
  };
}
